<template>
  <div id="knowledge-base-article">
    <div class="vx-row">
      <div class="vx-col w-full md:w-2/5 lg:w-1/4">
        <vx-card title="Related Questions">
          <ul class="bordered-items">
            <li
              v-for="que in resolvedRelatedQuestions"
              :key="que.id"
              class="py-2">
              <a
                href="#"
                @click.prevent="changeRelatedQuestion(que)">{{ que.title }}</a>
            </li>
          </ul>
        </vx-card>
      </div>
      <div class="vx-col w-full md:w-3/5 lg:w-3/4 mt-12 md:mt-0">
        <vx-card>
          <!-- Title -->
          <div class="article-title mb-6">
            <h3>{{ selectedQuestion.title }}</h3>
          </div>
          <div
            v-html="selectedQuestion.textBeforeImage"
            class="article-content" />

          <!-- Image -->
          <img
            v-if="selectedQuestion.img"
            :src="selectedQuestion.img"
            :alt="selectedQuestion.title"
            class="responsive rounded-lg my-6">

          <div
            v-html="selectedQuestion.textAfterImage"
            class="article-content" />

          <!-- Topics -->
          <div
            v-if="relatedTopics.length"
            class="mt-6">
            <h5 class="mb-2">
              Related topics
            </h5>
            <ul class="article-topics">
              <li
                v-for="(topic, index) in relatedTopics"
                :key="index"
                class="flex items-center">
                <feather-icon
                  icon="ChevronsRightIcon"
                  svg-classes="h-4 w-4"
                  class="mr-2" />
                <span>{{ topic }}</span>
              </li>
            </ul>
          </div>

          <!-- Footer -->
          <template slot="footer">
            <div class="flex items-center justify-between">
              <a
                v-if="previousArticle"
                href="#"
                class="flex items-center cursor-pointer"
                @click.prevent="prevArticle()">
                <feather-icon
                  :icon="$vs.rtl ? 'ChevronsRightIcon' : 'ChevronsLeftIcon'"
                  class="mr-2 cursor-pointer"
                  svgClasses="h-4 w-4"/>
                {{ $t('PrevArticle') }}
              </a>
              <div v-else></div> <!-- Placeholder for alignment -->
              <a
                v-if="nextArticle"
                href="#"
                class="flex items-center cursor-pointer"
                @click.prevent="nextArticleClick()">
                {{ $t('NextArticle') }}
                <feather-icon
                  :icon="$vs.rtl ? 'ChevronsLeftIcon' : 'ChevronsRightIcon'"
                  class="ml-2 cursor-pointer"
                  svgClasses="h-4 w-4"/>
              </a>
            </div>
          </template>
        </vx-card>
      </div>
    </div>
  </div>
</template>

<script>

import { mapActions, mapState } from 'vuex';

export default {
  i18n: {
    messages: {
      en: {
        PrevArticle: 'Previous Article',
        NextArticle: 'Next Article',
      },
    },
  },
  computed: {
    ...mapState({
      selectedCategory: (state) => state.documentation.selectedCategory,
      selectedSection: (state) => state.documentation.selectedSection,
      selectedQuestion: (state) => state.documentation.selectedQuestion,
    }),
    relatedTopics() {
      if (!this.selectedQuestion || !this.selectedQuestion.relatedQuestions) return [];
      const topics = this.selectedQuestion.relatedQuestions.map((related) => {
        const category = this.$store.state.documentation.data.find((c) => c.id === related.categoryId);
        if (!category) return null;
        const section = category.sections.find((s) => s.id === related.sectionId);
        return section ? section.title : null;
      });
      // Return unique, non-null topics
      return [...new Set(topics.filter((t) => t))];
    },
    resolvedRelatedQuestions() {
      if (!this.selectedQuestion || !this.selectedQuestion.relatedQuestions) return [];
      return this.selectedQuestion.relatedQuestions.map((related) => {
        const category = this.$store.state.documentation.data.find((c) => c.id === related.categoryId);
        if (!category) return null;
        const section = category.sections.find((s) => s.id === related.sectionId);
        if (!section) return null;
        return section.questions.find((q) => q.id === related.questionId);
      }).filter((q) => q); // Filter out nulls if any question isn't found
    },
    flatQuestions() {
      if (!this.selectedCategory) return [];
      return this.selectedCategory.sections.flatMap((section) => section.questions);
    },
    currentQuestionIndex() {
      if (!this.selectedQuestion) return -1;
      return this.flatQuestions.findIndex((q) => q.id === this.selectedQuestion.id && q.title === this.selectedQuestion.title);
    },
    previousArticle() {
      if (this.currentQuestionIndex > 0) {
        return this.flatQuestions[this.currentQuestionIndex - 1];
      }
      return null;
    },
    nextArticle() {
      if (this.currentQuestionIndex < this.flatQuestions.length - 1) {
        return this.flatQuestions[this.currentQuestionIndex + 1];
      }
      return null;
    },
  },
  mounted() {
    // this.$emit('changeRouteTitle', 'Question');
    // First, try to set the selected question from route params
    this.setSelectedQuestionByIds({
      categoryId: this.$route.params.category,
      sectionId: null,
      questionId: this.$route.params.question,
    });
    
    // Then check if everything is properly loaded
    this.$nextTick(() => {
      if (!this.selectedCategory || !this.selectedSection || !this.selectedQuestion) {
        this.$router.push('/documentation').catch(() => {});
      } else {
        // Set up proper breadcrumb for Documentation -> Category -> Article
        const breadcrumb = [
          {
            title: this.$t('$General.Documentation'),
            fullPath: '/documentation',
          },
          {
            title: this.selectedCategory.title,
            fullPath: `/documentation/${this.selectedCategory.id}`,
          },
          {
            title: this.selectedQuestion.title,
            fullPath: this.$route.fullPath,
          },
        ];
        this.$store.commit('SET_BREADCRUMB', breadcrumb);
        this.$store.dispatch('updateLatestHistoryItemTitle', this.selectedQuestion.title);
      }
    });
  },
  methods: {
    ...mapActions({
      updateLastBreadcrumbItemTitle: 'updateLastBreadcrumbItemTitle',
      setSelectedQuestionByIds: 'documentation/setSelectedQuestionByIds',
      prevQuestion: 'documentation/prevQuestion',
      nextQuestion: 'documentation/nextQuestion',
    }),
    goToArticle(question) {
      if (!question) return;

      const section = this.selectedCategory.sections.find((s) => s.questions.some((q) => q.id === question.id && q.title === question.title));

      if (section) {
        this.$store.dispatch('documentation/setSelectedQuestionInSection', { section, question });

        const questionEscaped = question.title
          ? question.title.toLowerCase().replaceAll(' ', '-')
          : question.id;
        const url = `/documentation/${this.selectedCategory.id}/${questionEscaped}`;

        this.$router.push(url).catch(() => {});
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    prevArticle() {
      this.goToArticle(this.previousArticle);
    },
    nextArticleClick() {
      this.goToArticle(this.nextArticle);
    },
    changeRelatedQuestion(question) {
      this.goToArticle(question);
    },
  },
};
</script>

<style lang="scss">
.article-content {
  ul {
    list-style-type: disc;
    padding-left: 2rem;
    margin-bottom: 1rem;

    li {
      margin-bottom: 0.5rem;
    }
  }
}
</style>
